import styles from 'styles/pages/modal/RegisterRoundModal.module.scss';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { TSelectObject } from 'types/dropdown';
import { useEffect, useState } from 'react';
import ModalWrapper from 'components/common/overlays/ModalWrapper';
import CustomDatepicker from 'components/common/display/CustomDatepicker';
import SingleBtn from 'components/common/elements/SingleBtn';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import MultipleSelectDropdown from 'components/common/elements/MultipleSelectDropdown';
import useModal from 'hooks/useModal';
import Checkbox from 'components/common/forms/Checkbox';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  getCourseListForRegisterChart,
  getRegisteredRoundDetail,
  postRegisterRound,
  putRegisteredRound,
} from 'apis/professor';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { TRegisterRoundInfoFormValues } from 'types/form/professor';
import { RegisterRoundDefaultValues } from 'constants/professor';
import { format } from 'date-fns';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import FormDropdown from 'components/common/forms/FormDropdown';
import Input from 'components/common/forms/Input';
import {
  TEditRegisteredRoundParams,
  TRegisterRoundParams,
} from 'types/apis/professor';
import { isTCustomAxiosError } from 'apis/config/axios';
import { getTodayDateType, isDateBeforeToday } from 'utils/date';

type TProps = {
  chartId?: number;
};

const RegisterRoundModal = ({ chartId }: TProps) => {
  const queryClient = useQueryClient();

  // 수정 시 필요한 실습 회차 데이터 가져오기 API
  const { data: getRegisteredRoundDetailData } = useQuery(
    'getRegisteredRoundDetail',
    () => getRegisteredRoundDetail(chartId as number),
    {
      onSuccess: ({ data: roundInfo }) => {
        const formValuesFromData = {
          chartName: roundInfo.chartName,
          chartRound: roundInfo.chartRound,
          chartType: roundInfo.chartType,
          courseName: roundInfo.courseName,
          firstRoundStartDate: setFirstRoundStartDate(
            getTodayDateType(roundInfo.firstSubmitStartDate)
          ),
          firstRoundEndDate: setFirstRoundEndDate(
            getTodayDateType(roundInfo.firstSubmitEndDate)
          ),
          secondRoundStartDate:
            roundInfo.secondSubmitStartDate &&
            setSecondRoundStartDate(
              getTodayDateType(roundInfo.secondSubmitStartDate)
            ),
          secondRoundEndDate:
            roundInfo.secondSubmitEndDate &&
            setSecondRoundEndDate(
              getTodayDateType(roundInfo.secondSubmitEndDate)
            ),
        };
        setSelectedChartList(roundInfo.chartType);
        setSecondRoundCheck(!!roundInfo.secondSubmitStartDate);
        setRegisteredChartId(roundInfo.registeredChartId);
        reset(formValuesFromData);
      },
      enabled: !!chartId,
    }
  );
  const { openModal, closeModal } = useModal();

  const today = new Date();

  const [showCourseList, setShowCourseList] = useState(false);
  const [showClassNameList, setShowClassNameList] = useState(false);
  const [secondRoundCheck, setSecondRoundCheck] = useState(false);
  const [firstRoundStartDate, setFirstRoundStartDate] = useState<Date | null>(
    chartId && getRegisteredRoundDetailData?.data.firstSubmitStartDate
      ? new Date(getRegisteredRoundDetailData.data.firstSubmitStartDate)
      : null
  );
  const [firstRoundEndDate, setFirstRoundEndDate] = useState<Date | null>(
    chartId && getRegisteredRoundDetailData?.data.firstSubmitEndDate
      ? new Date(getRegisteredRoundDetailData.data.firstSubmitEndDate)
      : null
  );
  const [secondRoundStartDate, setSecondRoundStartDate] = useState<Date | null>(
    chartId && getRegisteredRoundDetailData?.data.secondSubmitStartDate
      ? new Date(getRegisteredRoundDetailData.data.secondSubmitStartDate)
      : null
  );
  const [secondRoundEndDate, setSecondRoundEndDate] = useState<Date | null>(
    chartId && getRegisteredRoundDetailData?.data.secondSubmitEndDate
      ? new Date(getRegisteredRoundDetailData.data.secondSubmitEndDate)
      : null
  );
  const [showChartList, setShowChartList] = useState(false);
  const [selectedChartList, setSelectedChartList] = useState<
    Array<string | number>
  >([]);
  const [registeredChartId, setRegisteredChartId] = useState<number | null>();

  const chartList: TSelectObject[] = ChartInfo.map((chart) => ({
    label: chart.name,
    value: chart.type,
  }));

  const methods = useForm<TRegisterRoundInfoFormValues>({
    defaultValues: RegisterRoundDefaultValues,
  });

  const { handleSubmit, getValues, control, setValue, reset } = methods;

  const [courseInfoId, chartRound, chartName] = useWatch({
    name: ['courseInfoId', 'chartRound', 'chartName'],
    control,
  });

  // 교수 담당 강의 리스트 API
  const { data: getCourseListForRegisterChartData } = useQuery(
    'getCourseListForRegisterChart',
    getCourseListForRegisterChart
  );

  // 교수 담당 강의 리스트
  const courseList =
    getCourseListForRegisterChartData?.data.courseInfoForPostResponseList.map(
      (courseData) => {
        return {
          label: courseData.courseNameAndStudClass,
          value: courseData.courseInfoId,
        };
      }
    ) || [];

  // 실습 회차 등록 API
  const { mutate: postRegisterRoundMutation } = useMutation(postRegisterRound, {
    onSuccess: () => {
      openModal({
        title: '실습 회차 등록 완료',
        text: '회차가 등록 완료되었습니다. 실습 회차 등록 후 정보 수정은 제출 시작일 전까지 수정 가능하며 이후 변경을 원하는 경우, 카이아이 고객센터로 연락 부탁드립니다.',
        onSubmit: () => {
          queryClient.invalidateQueries('getRegisteredRoundList');
          closeModal();
        },
      });
    },
    onError: (error) => {
      if (isTCustomAxiosError(error)) {
        openModal({
          title: '회차 등록 실패',
          text: error.message,
        });
      }
    },
  });

  // 실습 회차 수정 API
  const { mutate: putRegisteredRoundMutation } = useMutation(
    (data: TEditRegisteredRoundParams) =>
      putRegisteredRound(registeredChartId as number, data),
    {
      onSuccess: () => {
        closeModal();
        queryClient.invalidateQueries('getRegisteredRoundList');
      },
      onError: (error) => {
        if (isTCustomAxiosError(error)) {
          openModal({
            title: '회차 수정 실패',
            text: error.message,
          });
        }
      },
    }
  );

  const onClickDropDownButton = (targetType: string) => {
    if (targetType === 'course') {
      setShowCourseList(!showCourseList);
    } else if (targetType === 'chart') {
      setShowChartList(!showChartList);
    } else if (targetType === 'className') {
      setShowClassNameList(!showClassNameList);
    }
  };

  const getCalendarStyle = (date?: Date, isSecondRound?: boolean) => {
    const isDatePassed = date && isDateBeforeToday(format(date, 'yyyyMMdd'));

    return {
      width: '208px',
      padding: '12px 16px',
      backgroundColor:
        (isSecondRound && (!secondRoundCheck || isDatePassed)) || isDatePassed
          ? STYLES_VARIABLES.GRAY_02
          : STYLES_VARIABLES.WHITE,
      color:
        (isSecondRound && (!secondRoundCheck || isDatePassed)) || isDatePassed
          ? STYLES_VARIABLES.GRAY_05
          : STYLES_VARIABLES.GRAY_06,
      fontWeight: '400',
    };
  };

  const onValid = () => {
    const data = getValues();
    if (data.courseInfoId === null || data.chartRound === null) return;

    if (
      firstRoundStartDate === null ||
      firstRoundEndDate === null ||
      selectedChartList.length === 0
    )
      return;

    const dataFromForm: TRegisterRoundParams = {
      ...data,
      courseInfoId: Number(data.courseInfoId),
      chartRound: Number(chartRound),
      chartTypeList: selectedChartList as number[],
      firstSubmitStartDate:
        (firstRoundStartDate as Date) &&
        format(firstRoundStartDate as Date, 'yyyy.MM.dd'),
      firstSubmitEndDate:
        (firstRoundEndDate as Date) &&
        format(firstRoundEndDate as Date, 'yyyy.MM.dd'),
      secondSubmitStartDate: secondRoundStartDate
        ? format(secondRoundStartDate, 'yyyy.MM.dd')
        : undefined,
      secondSubmitEndDate: secondRoundEndDate
        ? format(secondRoundEndDate, 'yyyy.MM.dd')
        : undefined,
    };

    const courseName =
      getCourseListForRegisterChartData?.data.courseInfoForPostResponseList.find(
        (course) => course.courseInfoId === data.courseInfoId
      )?.courseNameAndStudClass;

    const dataForEdit: TEditRegisteredRoundParams = {
      firstSubmitStartDate: firstRoundStartDate
        ? format(firstRoundStartDate, 'yyyy.MM.dd')
        : '',
      firstSubmitEndDate: firstRoundEndDate
        ? format(firstRoundEndDate, 'yyyy.MM.dd')
        : '',
      secondSubmitStartDate: secondRoundStartDate
        ? format(secondRoundStartDate, 'yyyy.MM.dd')
        : undefined,
      secondSubmitEndDate: secondRoundEndDate
        ? format(secondRoundEndDate, 'yyyy.MM.dd')
        : undefined,
    };

    if (chartId) {
      putRegisteredRoundMutation(dataForEdit);
    } else {
      openModal({
        isConfirm: true,
        title: '실습용 회차를 등록하시겠습니까?',
        text: `${courseName} 강의에 “(${data.chartRound}회차) ${data.chartName}" 회차를 등록하시겠습니까?`,
        onSubmit: () => {
          closeModal();
          data.courseInfoId &&
            postRegisterRoundMutation(dataFromForm as TRegisterRoundParams);
        },
      });
    }
  };

  useEffect(() => {
    if (courseInfoId) {
      const selectedCourse =
        getCourseListForRegisterChartData?.data.courseInfoForPostResponseList.find(
          (course) => course.courseInfoId === courseInfoId
        );

      setValue('chartRound', Number(selectedCourse?.chartRound));
    }
  }, [courseInfoId, getCourseListForRegisterChartData]);

  useEffect(() => {
    if (!secondRoundCheck) {
      setSecondRoundStartDate(null);
      setSecondRoundEndDate(null);
    }
  }, [secondRoundCheck]);

  useEffect(() => {
    if (getRegisteredRoundDetailData?.data.secondSubmitStartDate) {
      setSecondRoundCheck(true);
    } else {
      setSecondRoundCheck(false);
    }
  }, [getRegisteredRoundDetailData?.data.secondSubmitStartDate]);

  useEffect(() => {
    if (!chartId) {
      setSecondRoundCheck(false);
    }
  }, []);

  return (
    <>
      <ModalWrapper>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onValid)}>
            <div className="flex justify_between items_center">
              <h2 className={styles.title}>실습 회차 등록</h2>
              <button type="button" onClick={closeModal}>
                <CloseIcon fill={STYLES_VARIABLES.GRAY_07} />
              </button>
            </div>

            <p className={styles.first_main_text}>
              회차를 등록할 강의를 선택해 주세요.{' '}
              <span className={styles.color_text}>(필수)</span>
            </p>
            <p className={styles.sub_text}>
              해당 회차는 본 강의를 수강중인 학생들만 확인할 수 있습니다.
            </p>
            <div className="flex gap16 mt12">
              {!chartId ? (
                <FormDropdown
                  name="courseInfoId"
                  placeholder="강의를 선택해 주세요."
                  selectList={courseList}
                  btnStyle={{ width: '432px', height: '48px' }}
                  dropdownStyle={{ width: '432px' }}
                  onClick={() => onClickDropDownButton('course')}
                />
              ) : (
                <input
                  type="text"
                  name="courseName"
                  style={{
                    width: '432px',
                    height: '48px',
                    backgroundColor: STYLES_VARIABLES.GRAY_01,
                    border: `1px solid ${STYLES_VARIABLES.GRAY_05}`,
                    color: STYLES_VARIABLES.GRAY_06,
                    borderRadius: '8px',
                    padding: '0 16px',
                  }}
                  onClick={() => onClickDropDownButton('course')}
                  value={`${getRegisteredRoundDetailData?.data.courseName} || ${getRegisteredRoundDetailData?.data.studClass}`}
                  readOnly
                />
              )}
            </div>

            <p className={styles.main_text}>
              회차명을 입력해 주세요.{' '}
              <span className={styles.color_text}>(필수)</span>
            </p>
            <p className={styles.sub_text}>
              한글 영문, 숫자 포함 최대 20자까지 작성할 수 있습니다.
            </p>
            <div className="flex gap16">
              <Input type="text" name="chartRound" className="hidden" />
              <input
                type="text"
                value={chartRound ? `${chartRound}회차` : ''}
                className={styles.fixed_text}
                readOnly
              />
              <Input
                name="chartName"
                type="text"
                placeholderText="회차 이름을 작성해 주세요."
                className={styles.input}
                rules={{ required: true }}
                readOnly={chartId ? true : false}
                inputStyle={
                  chartId
                    ? {
                        backgroundColor: STYLES_VARIABLES.GRAY_01,
                        color: STYLES_VARIABLES.GRAY_06,
                        border: `1px solid ${STYLES_VARIABLES.GRAY_05}`,
                      }
                    : {}
                }
                maxLength={20}
              />
            </div>

            <p className={styles.main_text}>
              등록할 차트 종류를 선택해 주세요.{' '}
              <span className={styles.color_text}>(필수)</span>
            </p>
            <p className={styles.sub_text}>
              차트를 중복 선택하여 등록할 수 있습니다.
            </p>
            <div className={styles.input_wrapper}>
              <MultipleSelectDropdown
                lists={chartList}
                selectedChartList={
                  chartId ? selectedChartList : (selectedChartList as number[])
                }
                setSelectedChartList={setSelectedChartList}
                readOnly={chartId ? true : false}
                disabledStyle={{
                  backgroundColor: STYLES_VARIABLES.GRAY_01,
                  color: STYLES_VARIABLES.GRAY_06,
                }}
              />
            </div>

            <p className={styles.main_text}>
              제출 마감 기한을 선택해 주세요.{' '}
              <span className={styles.color_text}>(필수)</span>
            </p>
            <p className={styles.sub_text}>
              실습용 차트를 등록하시면 등록하신 날짜로부터 제출 시작 및 마감
              기한이 활성화됩니다.
            </p>
            <div className="mb16">
              <Checkbox
                text="1차 제출 마감일 설정"
                inputId="round1"
                labelStyle={{ fontWeight: '700', marginRight: '56px' }}
                checked={true}
                readOnly
              />
              <Checkbox
                text="2차 제출 마감일 설정"
                inputId="round2"
                labelStyle={{ fontWeight: '700' }}
                onChange={() => setSecondRoundCheck(!secondRoundCheck)}
                checked={secondRoundCheck}
                readOnly={
                  !!secondRoundStartDate &&
                  isDateBeforeToday(format(secondRoundStartDate, 'yyyyMMdd'))
                }
              />
            </div>
            <div className={styles.dropdown_group}>
              <div>
                <p className={styles.dropdown_label}>1차 제출 시작일</p>
                <CustomDatepicker
                  date={firstRoundStartDate}
                  setDate={setFirstRoundStartDate}
                  calendarStyle={getCalendarStyle(firstRoundStartDate as Date)}
                  leftIconFill={STYLES_VARIABLES.GRAY_08}
                  rightIconFill={STYLES_VARIABLES.GRAY_08}
                  minDate={new Date()}
                  placeholder={format(today, 'yyyy.MM.dd')}
                  readOnly={
                    chartId && firstRoundStartDate
                      ? isDateBeforeToday(
                          format(firstRoundStartDate, 'yyyyMMdd')
                        )
                      : false
                  }
                />
              </div>
              <div>
                <p className={styles.dropdown_label}>1차 제출 마감일</p>
                <CustomDatepicker
                  date={firstRoundEndDate}
                  setDate={setFirstRoundEndDate}
                  calendarStyle={getCalendarStyle(firstRoundStartDate as Date)}
                  leftIconFill={STYLES_VARIABLES.GRAY_08}
                  rightIconFill={STYLES_VARIABLES.GRAY_08}
                  minDate={firstRoundStartDate || today}
                  maxDate={secondRoundStartDate}
                  placeholder={format(today, 'yyyy.MM.dd')}
                  readOnly={
                    chartId && firstRoundStartDate
                      ? isDateBeforeToday(
                          format(firstRoundStartDate, 'yyyyMMdd')
                        )
                      : false
                  }
                />
              </div>
            </div>
            <div className={styles.dropdown_group}>
              <div>
                <p className={styles.dropdown_label}>2차 제출 시작일</p>
                <CustomDatepicker
                  date={secondRoundStartDate}
                  setDate={setSecondRoundStartDate}
                  calendarStyle={getCalendarStyle(
                    secondRoundStartDate as Date,
                    true
                  )}
                  leftIconFill={STYLES_VARIABLES.GRAY_08}
                  rightIconFill={STYLES_VARIABLES.GRAY_08}
                  minDate={firstRoundEndDate || today}
                  readOnly={
                    !secondRoundCheck ||
                    (secondRoundStartDate &&
                      isDateBeforeToday(
                        format(secondRoundStartDate as Date, 'yyyyMMdd')
                      ))
                      ? true
                      : false
                  }
                  placeholder={format(today, 'yyyy.MM.dd')}
                />
              </div>
              <div>
                <p className={styles.dropdown_label}>2차 제출 마감일</p>
                <CustomDatepicker
                  date={secondRoundEndDate}
                  setDate={setSecondRoundEndDate}
                  calendarStyle={getCalendarStyle(
                    secondRoundStartDate as Date,
                    true
                  )}
                  leftIconFill={STYLES_VARIABLES.GRAY_08}
                  rightIconFill={STYLES_VARIABLES.GRAY_08}
                  minDate={secondRoundStartDate || today}
                  readOnly={
                    !secondRoundCheck ||
                    (secondRoundStartDate &&
                      isDateBeforeToday(
                        format(secondRoundStartDate as Date, 'yyyyMMdd')
                      ))
                      ? true
                      : false
                  }
                  placeholder={format(today, 'yyyy.MM.dd')}
                />
              </div>
            </div>
            {secondRoundCheck ? (
              <p className={styles.sub_text}>
                2차 제출일 시작 전까지 1차 제출 차트에 대한 성적 마감을 완료해야
                합니다.
              </p>
            ) : (
              <p className={styles.height}></p>
            )}

            <div className="modal_btn_wrapper mt8">
              <SingleBtn
                btnText="취소"
                hasOutline={true}
                btnStyle={{
                  width: '100%',
                }}
                onClick={() =>
                  openModal({
                    isConfirm: true,
                    title: '실습 회차 등록 종료',
                    text: '해당 페이지에서 나가면 작성 중인 내용이 저장되지 않을 수 있습니다. 실습 회차 등록을 종료하시겠습니까?',
                    onSubmit: () => {
                      closeModal();
                    },
                    onCancel: () => {
                      setSecondRoundCheck(false);
                      return;
                    },
                  })
                }
              />
              <SingleBtn
                type="submit"
                btnText="등록"
                isBtnActive={true}
                btnStyle={{
                  width: '100%',
                }}
                disabled={
                  !chartId &&
                  (!courseInfoId ||
                    !chartRound ||
                    !chartName ||
                    selectedChartList.length === 0 ||
                    firstRoundStartDate === null ||
                    firstRoundEndDate === null)
                }
              />
            </div>
          </form>
        </FormProvider>
      </ModalWrapper>
    </>
  );
};

export default RegisterRoundModal;
